import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import AttributeItem from 'dashboard/components/AttributeItem';
import ClickableDetail from 'dashboard/components/ClickableDetail';
import AsyncSelfie from 'dashboard/components-lib/AsyncSelfie';
import {
  formatAddressFieldDisplay,
  formatDate,
  formatNormalizedPostalCode,
} from 'dashboard/lib/utils/formatters';
import { Account } from 'dashboard/models/account';
import { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';
import ImageAvatar from 'images/dashboard/avatar.svg';
import { FieldString } from 'lib/fields';
import { Iso8601DateString } from 'lib/types';

const AVATAR_WIDTH = '250px';
const AVATAR_HEIGHT = '250px';

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const LinkRow = styled('div')`
  grid-column-end: span 3;
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
  font-weight: ${FontWeight.Regular};
`;

const CardBodyFlex = styled(Card.Body)`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing[5]};
`;

const AvatarContainer = styled.div`
  flex: 0 0 ${AVATAR_WIDTH};
`;

const AttributeGridContainer = styled.div`
  flex: 1 0;
`;

interface ResourceAccountProps {
  account: Account;
  defaultAccordionOpen?: boolean;
}

const ResourceAccount = ({ account, defaultAccordionOpen = true }: ResourceAccountProps) => {
  // ===== Copy-pasta from AccountProfileSection.tsx =====
  const fieldAddress = account.attributes.fields['address']?.value as Record<string, FieldString>;

  const addressStreet1 = fieldAddress?.street_1?.value;
  const addressStreet2 = fieldAddress?.street_2?.value;
  const addressCity = fieldAddress?.city?.value;
  const addressSubdivision = fieldAddress?.subdivision?.value;
  const addressPostalCode = fieldAddress?.postal_code?.value;
  const addressCountryCode = fieldAddress?.country_code?.value;
  const birthdate = account.attributes.fields.birthdate?.value as Iso8601DateString;
  const phoneNumber = account.attributes.fields.phone_number?.value as string;
  const emailAddress = account.attributes.fields.email_address?.value as string;

  const {
    'name-full': nameFull,
    'name-full-localized': nameFullLocalized,
    'selfie-photo-url': selfiePhotoUrl,
  } = account.attributes;

  const formattedBirthdate =
    birthdate != null ? (
      <div>
        <p>{formatDate(birthdate)}</p>
      </div>
    ) : undefined;

  const formattedAge =
    birthdate != null ? (
      <p>{Math.floor(DateTime.local().diff(DateTime.fromISO(birthdate), 'years').years)}</p>
    ) : undefined;

  const normalizedPostalCode = formatNormalizedPostalCode({
    addressPostalCode: addressPostalCode,
    addressCountryCode: addressCountryCode,
  });

  const address = formatAddressFieldDisplay({
    addressStreet1: addressStreet1,
    addressStreet2: addressStreet2,
    addressCity: addressCity,
    addressSubdivision: addressSubdivision,
    addressPostalCode: normalizedPostalCode,
  });

  const isRedacted = account.attributes['redacted-at'] != null;

  return (
    <ClickableDetail
      header={
        <HeaderContainer>
          <Header>
            {(nameFullLocalized ?? nameFull) ? (
              <Heading size='sm'>{nameFullLocalized ?? nameFull}</Heading>
            ) : (
              <Heading size='sm' color='tertiary'>
                No name collected
              </Heading>
            )}
            <Spacer width={1} />
          </Header>
          <LinkRow>
            <StyledLink to={`/dashboard/accounts/${account.id}`}>{account.id}</StyledLink>
          </LinkRow>
        </HeaderContainer>
      }
      isDefaultOpen={defaultAccordionOpen}
    >
      <Card>
        <CardBodyFlex>
          <AvatarContainer>
            <AsyncSelfie
              height={AVATAR_HEIGHT}
              width={AVATAR_WIDTH}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              value={isRedacted ? [] : [{ url: selfiePhotoUrl }]}
              modalLabel='Account selfie'
              placeholder={
                <ImageAvatar
                  aria-label='placeholder_image'
                  height={AVATAR_HEIGHT}
                  width={AVATAR_WIDTH}
                />
              }
              sourceToken={account.id}
            />
          </AvatarContainer>
          <AttributeGridContainer>
            <AttributeGrid>
              <AttributeItem
                title='Name'
                value={nameFullLocalized ?? nameFull}
                isRedacted={isRedacted}
                placeholder='No name'
              />
              <AttributeItem
                title='Birthdate'
                value={formattedBirthdate}
                isRedacted={isRedacted}
                placeholder='No birthdate'
              />
              <AttributeItem
                title='Age'
                value={formattedAge}
                isRedacted={isRedacted}
                placeholder='&#8212;'
              />
              <AttributeItem
                title='Address'
                value={address}
                isRedacted={isRedacted}
                placeholder='No address'
              />
              <AttributeItem
                title='Phone number'
                value={phoneNumber}
                isRedacted={isRedacted}
                placeholder='No phone number'
              />
              <AttributeItem
                title='Email address'
                value={emailAddress}
                isRedacted={isRedacted}
                placeholder='No email address'
              />
            </AttributeGrid>
          </AttributeGridContainer>
        </CardBodyFlex>
      </Card>
    </ClickableDetail>
  );
};

export default ResourceAccount;
